import React from 'react'
import gql from 'graphql-tag'
import { Query, QueryResult } from 'react-apollo'
import { H4, Card } from '@blueprintjs/core'
import { SubCardsWrapper, SubCard } from './CounterSubCard'
import LoadingState from './LoadingState'
import NonIdealState from '../UI/NonIdealState'
import { compact, last } from 'lodash'
import { formatNumber } from './utils'
import Donut, { IDonutData } from '@ui/charts/Donut'
import { ResponsiveContainer } from 'recharts'

const employeeCounters = gql`
  query employeeCountersByCity($cities: [String]) {
    viewer {
      company {
        employees(cities: $cities, ignorePreferredLocations: true) {
          counters {
            seatedEmployees
            unseated
            noDeskNeeded: no_desk_needed
            byEmploymentType {
              name
              count
            }
          }
        }
      }
    }
  }
`

interface ICounter {
  name: string
  count: number
}

interface ICounters {
  seatedEmployees: number
  unseated: number
  noDeskNeeded: number
  byEmploymentType: ICounter[]
}

interface IPayload {
  viewer: {
    company: {
      employees: {
        counters: ICounters
      }
    }
  }
}

interface IVariables {
  cities: string[] | null
}

interface EmployeesCardProps {
  city: string | null
}

export default function EmployeesCard({ city }: EmployeesCardProps) {
  return (
    <Card style={{ width: '100%' }}>
      <H4>Employees</H4>
      <Query
        notifyOnNetworkStatusChange
        query={employeeCounters}
        variables={{ cities: compact([city]) }}
      >
        {({ loading, data }: QueryResult<IPayload>) => {
          if (loading || !data || !data.viewer) {
            return <LoadingState />
          }

          const { seatedEmployees, noDeskNeeded, unseated, byEmploymentType } =
            data.viewer.company.employees.counters

          const colors = ['128dee', '10b4df', '94e1ff', '26e8d4', '24c7a3']
          const pieData =
            // Sort data by count.
            // If more than 5 entries, group smallest into `Other`
            byEmploymentType.length > 5
              ? byEmploymentType
                  .sort((o1, o2) => o2.count - o1.count)
                  .reduce((result: IDonutData[], o, i) => {
                    if (result.length < 5) {
                      return [
                        ...result,
                        {
                          label: o.name,
                          value: o.count,
                          color: `#${colors[i]}`,
                        },
                      ]
                    }

                    return [
                      ...result.slice(0, result.length - 1),
                      {
                        label: 'Other',
                        value: (last(result)?.value || 0) + o.count,
                        color: `#${last(colors)}`,
                      },
                    ]
                  }, [])
              : byEmploymentType
                  .map((o, i) => {
                    return {
                      label: o.name,
                      value: o.count,
                      color: `#${colors[i]}`,
                    }
                  })
                  .sort((o1, o2) => o2.value - o1.value)

          return (
            <>
              {pieData.length ? (
                <ResponsiveContainer height={200} width="100%">
                  <Donut title="employeeType" data={pieData} height={200} />
                </ResponsiveContainer>
              ) : (
                <NonIdealState
                  iconProps={{
                    name: 'user',
                    color: '#ccc',
                    background: 'rgba(0,0,0,0.05)',
                  }}
                  message="To view employees in selected city, make sure they are seated first. Or select another city."
                />
              )}
              <SubCardsWrapper>
                <SubCard title="Seated" value={formatNumber(seatedEmployees)} />
                <SubCard title="Unseated" value={formatNumber(unseated)} />
                <SubCard title="No Desk Needed" value={formatNumber(noDeskNeeded)} />
              </SubCardsWrapper>
            </>
          )
        }}
      </Query>
    </Card>
  )
}
